import { Currency } from '@nemuru-eng/js-api-client';
import React, { useContext, useEffect } from 'react';

import { ENV_DARK_MODE } from '@/config/envVars';
import { setLocaleCurrency } from '@/contexts/ConfigContext/actions/setLocaleCurrency';
import { setPlatform } from '@/contexts/ConfigContext/actions/setPlatform';
import { updateConfigContext } from '@/contexts/ConfigContext/actions/updateConfigContext';

import { defaultLang, defaultRegion, defaultRegistry, dictionaryList } from '../../config/languages';
import { Layout, LoanListByProductConfig, Theme } from '../../config/types';
import { changeTheme } from './actions/changeTheme';
import { setComponentsConfig } from './actions/setComponentsConfig';
import { setCurrentLayout } from './actions/setCurrentLayout';
import { setLanguage } from './actions/setLanguage';
import { setRegion } from './actions/setRegion';
import { setRegistry } from './actions/setRegistry';
import { setShowDevelopOptionsPanel } from './actions/setShowDevelopOptionsPanel';

export type Platform = 'mac' | 'other';

export const configState = {
  language: defaultLang,
  region: defaultRegion,
  registry: defaultRegistry,
  localeCurrency: undefined as Currency | undefined,
  dictionaryValues: dictionaryList[defaultLang][defaultRegistry] as Record<string, string>,
  appTheme: undefined as Theme | undefined,
  themeName: null as null | string,
  darkMode: localStorage.getItem(ENV_DARK_MODE) === 'dark',
  componentsRegistry: undefined,
  currentLayout: undefined as Layout | undefined,
  componentsConfig: [] as LoanListByProductConfig[],
  showDevelopOptionsPanel: false as boolean,
  platform: undefined as Platform | undefined,
};

export type configContextState = typeof configState;

const actionsDefinitions = (setStateById: (id: string, stateChange: stateChangeInConfigContext) => void) => ({
  setLanguage: setLanguage(setStateById),
  setLocaleCurrency: setLocaleCurrency(setStateById),
  setRegion: setRegion(setStateById),
  setRegistry: setRegistry(setStateById),
  setComponentsConfig: setComponentsConfig(setStateById),
  changeTheme: changeTheme(setStateById),
  setCurrentLayout: setCurrentLayout(setStateById),
  setShowDevelopOptionsPanel: setShowDevelopOptionsPanel(setStateById),
  updateConfigContext: updateConfigContext(setStateById),
  setPlatform: setPlatform(setStateById),
});

const ConfigContext = React.createContext({
  state: configState,
  actions: actionsDefinitions(() => () => {}),
});

export type action = (setState: setStateInConfigContext, actionId: string) => () => void;

export type setConfigStateById = (id: string, stateChange: stateChangeInConfigContext) => void;
export type stateChangeInConfigContext = (prevState: typeof configState) => typeof configState;
export type setStateInConfigContext = React.Dispatch<stateChangeInConfigContext>;

export const useConfigContext = () => useContext(ConfigContext);

export function ConfigContextProvider(props) {
  const [state, setState] = React.useState(configState);
  const setStateById: setConfigStateById = (_, stateChange) => {
    setState(stateChange);
  };

  useEffect(() => {
    //@ts-expect-error: navigator is not defined in the server side
    const navigatorPlatform = navigator?.userAgentData?.platform || navigator?.platform || 'unknown';
    const platform = navigatorPlatform.toLowerCase().includes('mac') ? 'mac' : 'other';
    actions.setPlatform(platform);
  }, []);

  const actions = actionsDefinitions(setStateById);
  return <ConfigContext.Provider value={{ state, actions }}>{props.children}</ConfigContext.Provider>;
}
